import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
} from "react";
import { useToast } from "../../contexts/ToastContext";
import {
  DayPlanContextType,
  DayPlanState,
  DayPlanAction,
  PlanItem,
  CustomItemData,
  RawPlanItem,
} from "./types/dayPlan.types";
import {
  addDayPlanItem,
  updateDayPlanItem,
  deleteDayPlanItem,
  reorderDayPlanItems,
  getOfferDetails,
} from "../../services/api";
import { arrayMove } from "@dnd-kit/sortable";

export const PER_PERSON_UNIT = "os.";

const DayPlanContext = createContext<DayPlanContextType | null>(null);

function dayPlanReducer(
  state: DayPlanState,
  action: DayPlanAction
): DayPlanState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };

    case "SET_CURRENT_DAY":
      return { ...state, currentDayIndex: action.payload };

    case "SET_OFFER_DAYS":
      return { ...state, offerDays: action.payload };

    case "SET_PLAN_ITEMS":
      return { ...state, planItems: action.payload };

    case "REORDER_PLAN_ITEMS":
      return {
        ...state,
        planItems: arrayMove(
          state.planItems,
          action.payload.oldIndex,
          action.payload.newIndex
        ),
      };

    case "TOGGLE_CUSTOM_FORM":
      return {
        ...state,
        showCustomItemForm: !state.showCustomItemForm,
        activeDropdown: null,
      };

    case "SET_ACTIVE_DROPDOWN":
      return {
        ...state,
        activeDropdown: action.payload,
        showCustomItemForm: action.payload ? false : state.showCustomItemForm,
      };

    case "SET_EDITING_CELL":
      return { ...state, editingCell: action.payload };

    default:
      return state;
  }
}

const ACCOMMODATION_TYPE = "accommodation";

// Helper function to filter out accommodation items and map to PlanItem
function filterAndMapPlanItems(planItems: RawPlanItem[] = []): PlanItem[] {
  return planItems
    .filter((item) => item.categoryOption?.optionType !== ACCOMMODATION_TYPE)
    .map((item) => ({
      id: item.id,
      timeFrom: item.timeFrom || undefined,
      timeTo: item.timeTo || undefined,
      title: item.categoryOption?.name || item.customTitle || "Pozycja własna",
      quantity: item.quantity || 1,
      unit: getUnitForItem(item),
      unitPrice: item.unitPrice || 0,
      vatRate: item.vatRate,
      discountType: item.discountType,
      discountValue: item.discountValue,
      notes: item.notes,
      finalPrice: calculateFinalPrice(item),
      categoryOptionId: item.categoryOptionId,
      categoryType: getCategoryType(item),
      customOptionType: item.customOptionType as PlanItem["customOptionType"],
      sortOrder: item.sortOrder || 0,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getUnitForItem(item: Pick<RawPlanItem, "categoryOption">): string {
  if (!item.categoryOption) return "szt.";

  // Prefer configured unit over type-based fallback
  if (item.categoryOption.unit) {
    return item.categoryOption.unit;
  }

  switch (item.categoryOption.optionType) {
    case "gastronomy":
      return PER_PERSON_UNIT;
    case "conference":
      return "sala";
    case "additional":
      return "szt.";
    default:
      return "szt.";
  }
}

function getCategoryType(
  item: RawPlanItem
): "accommodation" | "gastronomy" | "conference" | "additional" | "custom" {
  if (item.categoryOption) {
    return item.categoryOption.optionType as "accommodation" | "gastronomy" | "conference" | "additional";
  }
  if (item.customOptionType) {
    return item.customOptionType as "accommodation" | "gastronomy" | "conference" | "additional";
  }
  return "custom";
}

function calculateFinalPrice(item: RawPlanItem): number {
  const basePrice = (item.quantity || 1) * (item.unitPrice || 0);

  if (!item.discountType || !item.discountValue) {
    return basePrice;
  }

  if (item.discountType === "percentage") {
    return basePrice * (1 - (item.discountValue || 0) / 100);
  } else if (item.discountType === "amount") {
    return Math.max(0, basePrice - (item.discountValue || 0));
  }

  return basePrice;
}

interface DayPlanProviderProps {
  children: React.ReactNode;
  offerId: string;
  initialOfferDays: any[];
  initialDayIndex?: number;
  numberOfGuests?: number;
  onOfferDaysChanged?: (offerDays: any[]) => void;
}

export const DayPlanProvider: React.FC<DayPlanProviderProps> = ({
  children,
  offerId,
  initialOfferDays,
  initialDayIndex = 0,
  numberOfGuests,
  onOfferDaysChanged,
}) => {
  const { addToast } = useToast();
  const [state, dispatch] = useReducer(dayPlanReducer, {
    isLoading: false,
    currentDayIndex: initialDayIndex,
    offerDays: initialOfferDays,
    planItems: filterAndMapPlanItems(
      initialOfferDays[initialDayIndex]?.planItems
    ),
    showCustomItemForm: false,
    editingCell: null,
    activeDropdown: null,
  });

  // Refresh offerDays from the server after any mutation to keep state in sync across day switches
  const refreshOfferDays = useCallback(async () => {
    const refreshedOffer = await getOfferDetails(offerId);
    dispatch({ type: "SET_OFFER_DAYS", payload: refreshedOffer.offerDays });
    onOfferDaysChanged?.(refreshedOffer.offerDays);
    const updatedPlanItems = filterAndMapPlanItems(
      refreshedOffer.offerDays[state.currentDayIndex]?.planItems
    );
    dispatch({ type: "SET_PLAN_ITEMS", payload: updatedPlanItems });
  }, [offerId, state.currentDayIndex, onOfferDaysChanged]);

  const setCurrentDay = useCallback(
    (index: number) => {
      dispatch({ type: "SET_CURRENT_DAY", payload: index });
      const newDayItems = filterAndMapPlanItems(
        state.offerDays[index]?.planItems
      );
      dispatch({ type: "SET_PLAN_ITEMS", payload: newDayItems });
    },
    [state.offerDays]
  );

  const addPlanItem = useCallback(
    async (itemData: any) => {
      if (!state.offerDays[state.currentDayIndex]) return;

      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const currentDay = state.offerDays[state.currentDayIndex];
        await addDayPlanItem(offerId, currentDay.id, itemData);
        try {
          await refreshOfferDays();
        } catch (refreshError) {
          console.warn("Add succeeded but state refresh failed. Data will sync on next operation.", refreshError);
        }
      } catch (error) {
        console.error("Failed to add plan item:", error);
        throw error;
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [offerId, state.currentDayIndex, state.offerDays, refreshOfferDays]
  );

  const addCustomItem = useCallback(
    async (customData: CustomItemData) => {
      const itemData = {
        customTitle: customData.customTitle,
        timeFrom: customData.timeFrom,
        timeTo: customData.timeTo,
        quantity: customData.quantity || 1,
        unitPrice: customData.unitPrice || 0,
        vatRate: customData.vatRate,
        notes: customData.notes,
        unit: customData.unit,
        categoryOptionId: undefined,
        customOptionType: customData.customOptionType || undefined,
      };

      await addPlanItem(itemData);
      dispatch({ type: "TOGGLE_CUSTOM_FORM" });
    },
    [addPlanItem]
  );

  const updatePlanItem = useCallback(
    async (itemId: string, updates: any) => {
      if (!state.offerDays[state.currentDayIndex]) return;

      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const currentDay = state.offerDays[state.currentDayIndex];
        await updateDayPlanItem(offerId, currentDay.id, itemId, updates);
        try {
          await refreshOfferDays();
        } catch (refreshError) {
          console.warn("Update succeeded but state refresh failed. Data will sync on next operation.", refreshError);
        }
      } catch (error) {
        console.error("Failed to update plan item:", error);
        throw error;
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [offerId, state.currentDayIndex, state.offerDays, refreshOfferDays]
  );

  const deletePlanItem = useCallback(
    async (itemId: string) => {
      if (!state.offerDays[state.currentDayIndex]) return;

      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const currentDay = state.offerDays[state.currentDayIndex];
        await deleteDayPlanItem(offerId, currentDay.id, itemId);
        try {
          await refreshOfferDays();
        } catch (refreshError) {
          console.warn("Delete succeeded but state refresh failed. Data will sync on next operation.", refreshError);
        }
      } catch (error) {
        console.error("Failed to delete plan item:", error);
        throw error;
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [offerId, state.currentDayIndex, state.offerDays, refreshOfferDays]
  );

  const reorderItems = useCallback(
    async (oldIndex: number, newIndex: number) => {
      if (!state.offerDays[state.currentDayIndex]) return;

      dispatch({ type: "SET_LOADING", payload: true });
      try {
        // Optimistic update for immediate UI feedback
        dispatch({
          type: "REORDER_PLAN_ITEMS",
          payload: { oldIndex, newIndex },
        });

        const reorderedItems = arrayMove(state.planItems, oldIndex, newIndex);
        const reorderData = reorderedItems.map((item, index) => ({
          id: item.id,
          sortOrder: index,
        }));

        const currentDay = state.offerDays[state.currentDayIndex];
        await reorderDayPlanItems(offerId, currentDay.id, reorderData);
        try {
          await refreshOfferDays();
        } catch (refreshError) {
          console.warn("Reorder succeeded but state refresh failed. Data will sync on next operation.", refreshError);
        }
      } catch (error) {
        console.error("Failed to reorder items:", error);
        dispatch({
          type: "REORDER_PLAN_ITEMS",
          payload: { oldIndex: newIndex, newIndex: oldIndex },
        });
        throw error;
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [offerId, state.currentDayIndex, state.offerDays, state.planItems, refreshOfferDays]
  );

  const copyFromPreviousDay = useCallback(async () => {
    if (state.currentDayIndex === 0) return;

    const previousDay = state.offerDays[state.currentDayIndex - 1];
    const currentDay = state.offerDays[state.currentDayIndex];

    if (!previousDay || !currentDay) return;

    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const previousPlanItems =
        previousDay.planItems?.filter(
          (item: RawPlanItem) => item.categoryOption?.optionType !== ACCOMMODATION_TYPE
        ) || [];

      if (previousPlanItems.length === 0) {
        addToast({ type: "info", message: "Poprzedni dzień nie ma elementów do skopiowania" });
        return;
      }

      // Copy each item individually to preserve per-item quantities
      // (bulk add with defaultQuantity=1 loses per-person quantities like "Obiad serwowany")
      for (const item of previousPlanItems) {
        if (item.categoryOptionId) {
          await addDayPlanItem(offerId, currentDay.id, {
            categoryOptionId: item.categoryOptionId,
            timeFrom: item.timeFrom,
            timeTo: item.timeTo,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            notes: item.notes,
          });
        } else {
          await addDayPlanItem(offerId, currentDay.id, {
            customTitle: item.customTitle,
            timeFrom: item.timeFrom,
            timeTo: item.timeTo,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            notes: item.notes,
            categoryOptionId: undefined,
            customOptionType: item.customOptionType || undefined,
          });
        }
      }

      try {
        await refreshOfferDays();
      } catch (refreshError) {
        console.warn("Copy succeeded but state refresh failed. Data will sync on next operation.", refreshError);
      }
    } catch (error) {
      console.error("Failed to copy from previous day:", error);
      addToast({ type: "error", message: "Nie udało się skopiować elementów z poprzedniego dnia" });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [offerId, state.currentDayIndex, state.offerDays, addToast, refreshOfferDays]);

  const toggleCustomItemForm = useCallback(() => {
    dispatch({ type: "TOGGLE_CUSTOM_FORM" });
  }, []);

  const setActiveDropdown = useCallback((type: string | null) => {
    dispatch({ type: "SET_ACTIVE_DROPDOWN", payload: type });
  }, []);

  const startEditingCell = useCallback((itemId: string, field: string) => {
    dispatch({ type: "SET_EDITING_CELL", payload: { itemId, field } });
  }, []);

  const stopEditingCell = useCallback(() => {
    dispatch({ type: "SET_EDITING_CELL", payload: null });
  }, []);

  const value: DayPlanContextType = {
    offerId,
    currentDayIndex: state.currentDayIndex,
    offerDays: state.offerDays,
    planItems: state.planItems,
    numberOfGuests,
    isLoading: state.isLoading,
    showCustomItemForm: state.showCustomItemForm,
    editingCell: state.editingCell,
    activeDropdown: state.activeDropdown,
    setCurrentDay,
    addPlanItem,
    addCustomItem,
    updatePlanItem,
    deletePlanItem,
    reorderItems,
    copyFromPreviousDay,
    toggleCustomItemForm,
    setActiveDropdown,
    startEditingCell,
    stopEditingCell,
  };

  return (
    <DayPlanContext.Provider value={value}>{children}</DayPlanContext.Provider>
  );
};

export const useDayPlan = (): DayPlanContextType => {
  const context = useContext(DayPlanContext);
  if (!context) {
    throw new Error("useDayPlan must be used within a DayPlanProvider");
  }
  return context;
};
